import React, { useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { Box, Typography } from "@mui/material";

type AppSearchDropDownProps = {
  required?: boolean;
  isMultiSelect: boolean;
  title: string;
  textFieldValue?: string;
  value?: FilterValue[];
  list: FilterValue[];
  local: boolean;
  fontSize?: string;
  handleSearch?: (searchText?: string) => void;
  handleSelect?: (selectedItems: FilterValue[]) => void;
  disabled?: boolean;
  minWidth?: string;
  inputHeight?: string;
  borderColor?: string;
  background?: string;
  maxWidth?: string;
  borderRadius?: string;
  sx?: any;
  showId?: boolean;
};

export default function AppSearchDropDown({
  required,
  isMultiSelect,
  title,
  textFieldValue,
  list,
  value,
  local,
  fontSize,
  handleSearch,
  handleSelect,
  disabled,
  minWidth,
  borderColor = "#ddd",
  background,
  maxWidth,
  borderRadius,
  sx,
  showId = false,
}: AppSearchDropDownProps) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly FilterValue[]>([]);
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (!local) {
      onSearchComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
      setLoading(false);
      setOptions([...list]);
    }
  };

  const onSearchComplete = () => {
    setLoading(false);
    setOptions([...list]);
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;

    if (!open) return;
    if (local) {
      const PATTERN = new RegExp(searchValue, "i");
      const filteredValues = list.filter((item) => PATTERN.test(item.value));
      setOptions(filteredValues);
    } else {
      setLoading(true);
      handleSearch?.(searchValue);
    }
  };

  return (
    <Box>
      <Autocomplete
        sx={{
          padding: 0,
          margin: 0,
          minWidth: minWidth,
          fontSize: fontSize,
          maxWidth: maxWidth,
          borderRadius: borderRadius,
          background: background,
          "& .MuiOutlinedInput-root": {
            borderColor: borderColor,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor,
            },
            "&:hover fieldset": {
              borderColor: "#6455ED",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#6455ED",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#93969B",
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#6455ED",
          },
          ...sx,
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) =>
          showId ? `${option.id} - ${option.value}` : option.value
        }
        options={options}
        loading={loading}
        noOptionsText={
          <Typography sx={{ fontSize: fontSize }}>No options</Typography>
        }
        loadingText={
          <Typography sx={{ fontSize: fontSize }}>Loading...</Typography>
        }
        inputValue={textFieldValue}
        value={value}
        multiple={true}
        disabled={disabled}
        onChange={(event, value: FilterValue[]) => {
          if (handleSelect) {
            if (value) {
              if (isMultiSelect) {
                handleSelect(value);
              } else {
                handleSelect(
                  value.length === 0
                    ? []
                    : value.slice(value.length - 1, value.length),
                );
              }
            }
          }
        }}
        onInputChange={(event, newInputValue) => {
          if (newInputValue === "") {
            setLoading(false);
          }
        }}
        ChipProps={{
          sx: {
            fontSize: "0.8rem",
            height: "17.5px",
            fontWeight: 500,
            borderRadius: "3px",
          },
          size: "small",
        }}
        ListboxProps={{ sx: { fontSize: fontSize, zIndex: 99999 } }}
        disablePortal={false}
        componentsProps={{
          popper: {
            style: { zIndex: 99999 },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={title}
            required={required}
            size="small"
            onChange={handleInputChange}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={10} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                style: { fontSize: fontSize },
              },
              inputLabel: {
                ...params.InputLabelProps,
                style: { fontSize: fontSize },
              },
            }}
          />
        )}
      />
    </Box>
  );
}
